package org.btmonier.games

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.MainScope
import kotlinx.coroutines.launch
import kotlinx.html.*
import kotlinx.html.dom.append
import kotlinx.html.dom.create
import org.btmonier.games.components.*
import org.btmonier.games.model.*
import org.w3c.dom.Element
import org.w3c.dom.HTMLElement

private val scope = MainScope()

private var currentPage = "games"
private var siteConfig: SiteConfig? = null
private var gamesData: List<Game>? = null
private var currentGame: Game? = null

fun main() {
    console.log("Games app: main() called")
    // Use DOMContentLoaded for faster initialization
    // This fires when the DOM is ready, before images/stylesheets finish loading
    val readyState = document.readyState.asDynamic().toString() as String
    console.log("Games app: document.readyState = $readyState")
    if (readyState == "complete" || readyState == "interactive") {
        // DOM is already ready, initialize immediately
        console.log("Games app: DOM already ready, initializing immediately")
        initializeApp()
    } else {
        // Wait for DOM to be ready
        console.log("Games app: Waiting for DOMContentLoaded")
        document.addEventListener("DOMContentLoaded", {
            console.log("Games app: DOMContentLoaded fired, initializing")
            initializeApp()
        })
    }
}

private fun initializeApp() {
    console.log("Games app: initializeApp() called")
    showLoading()
    
    scope.launch {
        try {
            console.log("Games app: Loading site config...")
            // Load all content
            siteConfig = ContentLoader.loadSiteConfig()
            console.log("Games app: Site config loaded: ${siteConfig?.name}")
            
            console.log("Games app: Loading games...")
            gamesData = ContentLoader.loadGames()
            console.log("Games app: Loaded ${gamesData?.size ?: 0} games")
            
            // Get initial page from hash
            val hash = window.location.hash.removePrefix("#")
            when {
                hash.startsWith("game-") -> {
                    val slug = hash.removePrefix("game-")
                    val game = gamesData!!.find { it.metadata.slug == slug }
                    if (game != null) {
                        currentGame = game
                        currentPage = "game"
                    } else {
                        currentPage = "games"
                    }
                }
                hash.isNotEmpty() -> currentPage = hash
                else -> currentPage = "games"
            }
            
            // Render the application
            renderApp()
            
            // Setup navigation listeners
            window.onhashchange = { 
                val newHash = window.location.hash.removePrefix("#")
                when {
                    newHash.startsWith("game-") -> {
                        val slug = newHash.removePrefix("game-")
                        val game = gamesData?.find { it.metadata.slug == slug }
                        if (game != null) {
                            currentGame = game
                            currentPage = "game"
                            renderPage()
                        } else {
                            navigateToPage("games")
                        }
                    }
                    newHash.isNotEmpty() && newHash != currentPage -> {
                        currentPage = newHash
                        renderPage()
                        updateActiveNavItems()
                    }
                }
            }
            
        } catch (e: Exception) {
            showError("Failed to load content: ${e.message}")
            console.error("Error loading content:", e)
        }
    }
}

private fun showLoading() {
    document.body?.innerHTML = ""
    document.body?.append {
        div("loading") {
            div("loading-spinner") {}
            p("loading-text") { +"Loading..." }
        }
    }
}

private fun showError(message: String) {
    document.body?.innerHTML = ""
    document.body?.append {
        div {
            id = "error-container"
            style = """
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                min-height: 100vh;
                padding: 24px;
                text-align: center;
            """.trimIndent()
            span("material-icons") {
                style = "font-size: 64px; color: var(--md-sys-color-error); margin-bottom: 16px;"
                +"error_outline"
            }
            h2 { 
                style = "margin-bottom: 8px;"
                +"Something went wrong" 
            }
            p { 
                style = "color: var(--md-sys-color-on-surface-variant);"
                +message 
            }
            button(classes = "md-button md-button-filled") {
                id = "retry-button"
                style = "margin-top: 24px;"
                +"Retry"
            }
        }
    }
    
    document.getElementById("retry-button")?.addEventListener("click", {
        window.location.reload()
    })
}

private fun renderApp() {
    document.body?.innerHTML = ""
    document.title = "${getPageTitle(currentPage)} - ${siteConfig?.name ?: "Games"}"
    
    val appLayout = document.create.div("app-layout") {
        id = "app-layout"
    }
    document.body?.appendChild(appLayout)
    
    // Create Navigation Bar
    appLayout.appendChild(createNavBar())
    
    // Create Main Content Area
    val mainContent = document.create.div("main-content") {
        id = "main-content"
    }
    appLayout.appendChild(mainContent)
    
    // Create Page Container
    val pageContainer = document.create.div("page-container") {
        id = "page-container"
    }
    mainContent.appendChild(pageContainer)
    
    // Create Footer
    appLayout.appendChild(createFooter())
    
    // Render current page
    renderPage()
    
    // Setup nav click handlers
    setupNavigation()
}

private fun createNavBar(): HTMLElement {
    val config = siteConfig!!
    
    return document.create.nav("nav-bar") {
        id = "nav-bar"
        
        // Brand/Logo
        div("nav-bar-brand") {
            div("nav-bar-logo") {
                id = "nav-logo"
                title = config.name
                img(classes = "nav-logo-img", src = "images/avatar.svg", alt = config.name)
            }
            div("nav-bar-title-group") {
                span("nav-bar-title") { +"Brandon Monier" }
                span("nav-bar-subtitle") { +"(games)" }
            }
        }
        
        // Navigation Items
        div("nav-bar-items") {
            a(href = "#all-games", classes = "nav-bar-item") {
                attributes["data-page"] = "all-games"
                if (currentPage == "all-games") classes = classes + " active"
                span("material-icons") { +"sports_esports" }
                span("nav-bar-item-label") { +"All Games" }
            }
        }
    }
}

private fun createFooter(): HTMLElement {
    return document.create.footer("site-footer") {
        id = "site-footer"
        
        div("footer-content") {
            // Left side - avatar and copyright
            div("footer-left") {
                img(src = "images/avatar.svg", alt = "Brandon Monier", classes = "footer-avatar") {}
                span("footer-copyright") {
                    +"© ${kotlin.js.Date().getFullYear()} Brandon Monier"
                }
            }
            
            // Right side - version and GitHub link
            div("footer-right") {
                span("footer-version") {
                    +"v${BuildConfig.VERSION}"
                }
                a(href = "https://github.com/btmonier/btm_site_games", target = "_blank", classes = "footer-github") {
                    title = "View source on GitHub"
                    i("fa-brands fa-github") {}
                }
            }
        }
    }
}

private fun renderPage() {
    val container = document.getElementById("page-container") ?: return
    container.innerHTML = ""
    
    // Update document title
    val pageTitle = when (currentPage) {
        "game" -> currentGame?.metadata?.title ?: "Game"
        else -> getPageTitle(currentPage)
    }
    document.title = "$pageTitle - ${siteConfig?.name ?: "Games"}"
    
    val pageContent: HTMLElement = when (currentPage) {
        "games" -> createGameListPage(siteConfig!!, gamesData!!)
        "all-games" -> createAllGamesPage(gamesData!!)
        "game" -> {
            val game = currentGame ?: gamesData!!.first()
            createGamePage(game)
        }
        else -> createGameListPage(siteConfig!!, gamesData!!)
    }
    
    container.appendChild(pageContent)
    
    // Setup game card click handlers if on list page
    // Note: For search results, handlers are set up dynamically in GameListPage
    if (currentPage == "games" || currentPage == "all-games") {
        setupGameCardHandlers()
    }
}

private fun setupNavigation() {
    // Setup click handlers for nav items
    document.querySelectorAll(".nav-bar-item").asList().forEach { element ->
        element.addEventListener("click", { e ->
            e.preventDefault()
            val page = element.getAttribute("data-page") ?: "games"
            navigateToPage(page)
        })
    }
    
    // Logo click navigates to front page (no hash)
    document.getElementById("nav-logo")?.addEventListener("click", {
        currentPage = "games"
        window.history.pushState(null, "", window.location.pathname)
        renderPage()
        updateActiveNavItems()
        window.scrollTo(0.0, 0.0)
    })
}

private fun setupGameCardHandlers() {
    document.querySelectorAll(".game-card").asList().forEach { card ->
        card.addEventListener("click", { e ->
            e.preventDefault()
            val slug = card.getAttribute("data-slug") ?: return@addEventListener
            navigateToGame(slug)
        })
    }
}

private fun navigateToPage(page: String) {
    if (page == currentPage) return
    
    currentPage = page
    window.history.pushState(null, "", "#$page")
    renderPage()
    updateActiveNavItems()
    
    // Scroll to top
    window.scrollTo(0.0, 0.0)
}

private fun navigateToGame(slug: String) {
    val game = gamesData?.find { it.metadata.slug == slug }
    if (game != null) {
        currentGame = game
        currentPage = "game"
        window.history.pushState(null, "", "#game-$slug")
        renderPage()
        window.scrollTo(0.0, 0.0)
    }
}

private fun updateActiveNavItems() {
    document.querySelectorAll(".nav-bar-item").asList().forEach { element ->
        val page = element.getAttribute("data-page")
        if (page == currentPage || (currentPage == "game" && page == "all-games")) {
            element.classList.add("active")
        } else {
            element.classList.remove("active")
        }
    }
}

private fun getPageTitle(page: String): String {
    return when (page) {
        "games" -> "Game Search"
        "all-games" -> "All Games"
        "game" -> "Game"
        else -> "Game Search"
    }
}

// Extension function to convert NodeList to List
private fun org.w3c.dom.NodeList.asList(): List<Element> {
    val list = mutableListOf<Element>()
    for (i in 0 until this.length) {
        this.item(i)?.let { list.add(it as Element) }
    }
    return list
}
